import { useEffect, useMemo, useCallback, useState } from "react";
import { pushAnalyticsEvent } from "@ottatech/analytics";
import { TipType } from "@ottatech/design";
import { useQuery, useMutation } from "@apollo/client";

import { ProfilePerformanceNotification } from "./types/ProfilePerformance";
import { DashboardPromptNotification } from "./PromptNotification";

import {
  NotificationDocument,
  DismissNotificationDocument,
  Notification,
} from "schema";
import { handleMutationError } from "utils/error";

const getNotification = (notification: string | null) => {
  switch (notification) {
    case Notification.ProfilePerformance:
      return ProfilePerformanceNotification;
    default:
      return null;
  }
};

export interface IDashboardPromptNotification {
  key: string;
  name: string;
  level: TipType;
  Content: React.ElementType;
  dismissable?: boolean;
}

export const PromptNotification = ({
  className,
}: {
  className?: string;
}): React.ReactElement | null => {
  const [analyticsData, setAnalyticsData] = useState({});
  const { data, loading, refetch } = useQuery(NotificationDocument, {
    fetchPolicy: "cache-and-network",
  });

  const [dismissMutation] = useMutation(DismissNotificationDocument, {
    onError: handleMutationError,
  });

  const notification = useMemo(() => {
    return getNotification(data?.me?.notification ?? null);
  }, [data]);

  const handleButtonClick = (notification: IDashboardPromptNotification) => {
    pushAnalyticsEvent({
      eventName: "Candidate Clicked Dashboard Prompt",
      key: notification.key,
      ...analyticsData,
    });
  };

  const dismissNotification = useCallback(async () => {
    if (notification) {
      pushAnalyticsEvent({
        eventName: "Candidate Dismissed Dashboard Prompt",
        key: notification.key,
        ...analyticsData,
      });
      await dismissMutation({
        variables: { notificationName: notification.name },
      });
      await refetch();
    }
  }, [analyticsData, dismissMutation, notification, refetch]);

  useEffect(() => {
    if (notification) {
      pushAnalyticsEvent({
        eventName: "Candidate Viewed Dashboard Prompt",
        key: notification.key,
        ...analyticsData,
      });
    }
  }, [analyticsData, notification]);

  if (loading || !data) {
    return null;
  }

  if (notification) {
    return (
      <DashboardPromptNotification
        className={className}
        key={notification.key}
        level={notification.level}
        onLoad={data => setAnalyticsData(data)}
        onDismiss={dismissNotification}
        onButtonClick={() => handleButtonClick(notification)}
        Component={notification.Content}
      />
    );
  }

  return null;
};
